<template>
  <ui-modal-layout
    :title="brand ? tr('Delete brand') : tr('Disconnect Confirmation')"
    :icon="brand ? Trash2Icon : UnplugIcon"
    tone="destructive"
  >
    <p class="m-0 text-muted-foreground">
      {{ modalDescription }}
      <template v-if="showSecondaryQuestion">
        <br>
        {{ brand ? tr('Do you really want to delete it?') : tr('Do you really want to disconnect it?') }}
      </template>
    </p>

    <info-text v-if="deactivateHintIsVisible">
      {{ tr('If you do not need this brand for a while, you can deactivate it instead of deleting it: its automations stop and it does not count towards the brand limit of your subscription.') }}
    </info-text>

    <div v-if="defuncRules.length" class="flex flex-col divide-y divide-border rounded-lg border border-border">
      <div v-for="row in defuncRules" :key="row.id" class="flex items-center gap-3 px-3 py-2.5">
        <switcher v-model="row.active" disabled />
        <rule-type-badge compact :action="row.action" />
        <span class="min-w-0 flex-1 text-sm text-foreground">{{ row.name }}</span>
      </div>
    </div>

    <template #footer>
      <ui-button variant="outline" :disabled="isLoading" @click="() => close()">
        {{ tr('Cancel') }}
      </ui-button>
      <ui-button variant="destructive" :disabled="isLoading" @click="() => confirm()">
        <loader-circle-icon v-if="isLoading" class="animate-spin" />
        {{ tr('Confirm') }}
      </ui-button>
    </template>
  </ui-modal-layout>
</template>

<script>
import { LoaderCircle, Trash2, Unplug } from '@lucide/vue';
import { markRaw } from 'vue';

import AdAccount from '@/entities/AdAccount';
import Brand from '@/entities/Brand.js';
import ConnectedProfile from '@/entities/ConnectedProfile';
import SourceProfile from '@/entities/SourceProfile.js';
import InfoText from '@/ui/components/InfoText.vue';
import RuleTypeBadge from '@/ui/components/RuleTypeBadge.vue';
import Switcher from '@/ui/components/Switcher.vue';

export default {
  name: 'DisconnectConfirmModal',
  components: {
    InfoText,
    LoaderCircleIcon: LoaderCircle,
    RuleTypeBadge,
    Switcher,
  },
  props: {
    connectedProfile: {
      type: ConnectedProfile,
      required: false,
      default: null,
    },
    sourceProfile: {
      type: SourceProfile,
      required: false,
      default: null,
    },
    adAccount: {
      type: AdAccount,
      required: false,
      default: null,
    },
    brand: {
      type: Brand,
      required: false,
      default: null,
    },
    defuncRules: {
      type: Array,
      required: true,
      default: () => [],
    },
    confirmCallback: {
      type: Function,
      required: true,
    },
  },
  data: () => ({
    Trash2Icon: markRaw(Trash2),
    UnplugIcon: markRaw(Unplug),
    isLoading: false,
  }),
  computed: {
    showSecondaryQuestion() {
      if (this.brand && this.defuncRules.length === 0) {
        return false;
      }

      return true;
    },
    deactivateHintIsVisible() {
      return !!this.brand && !this.brand.isInactive && !this.brand.is_managed;
    },
    shouldUseCompactBody() {
      return this.defuncRules.length === 0;
    },
    modalDescription() {
      if (this.connectedProfile) {
        return this.tr('The following automations stop when you disconnect the :profileName profile, because no other profile reaches the assets they use.', {
          ':profileName': this.connectedProfile.name,
        });
      }

      if (this.brand) {
        if (this.defuncRules.length > 0) {
          return this.tr('The following automations will be deleted with the deletion of the :brandName brand.', {
            ':brandName': this.brand.name,
          });
        }
        return this.tr('Are you sure you want to delete the :brandName brand?', {
          ':brandName': this.brand.name,
        });
      }

      let assetName = '';
      let assetTypeString = '';
      if (this.sourceProfile) {
        assetName = this.sourceProfile.name;
        assetTypeString = this.tr('brand');
      }
      else if (this.adAccount) {
        assetName = this.adAccount.name;
        assetTypeString = this.tr('ad account');
      }

      return this.tr('The following automations will become inactive with the disconnection of the :assetName :assetType.', {
        ':assetName': assetName,
        ':assetType': assetTypeString,
      });
    },
  },
  methods: {
    close() {
      this.$emit('close');
    },
    confirm() {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;

      this.confirmCallback()
        .then(() => this.close())
        .catch(err => this.error(err))
        .finally(() => this.isLoading = false);
    },
  },
};
</script>
